import { schema } from 'normalizr'
import { omit, get, uniq } from 'lodash-es'
import slugify from 'slugify'
import queryString from 'query-string'
import { sortObject } from '../util/object'

export const feed = new schema.Entity(
  'feeds',
  {},
  {
    processStrategy: f => {
      const queryObject = sortObject(queryString.parseUrl(f.feed_url).query)
      const viewSettings = get(f, 'view_settings', {}) || {}
      return {
        ...omit(f, 'feed_url'),
        query: {
          object: queryObject,
          string: queryString.stringify(queryObject)
        },
        slug: `${f.id}----${slugify(f.name, { lower: true })}`,
        url: f.feed_url,
        viewSettings: {
          hiddenInfo: viewSettings.hidden_info || [],
          removedInfo: viewSettings.removed_info || [],
          thumbnailView:
            get(f, 'view_settings.thumbnail_type', get(f, 'view_settings.thumbnail_type')) === 'square'
              ? 'squared'
              : get(f, 'view_settings.thumbnail_type', get(f, 'view_settings.thumbnail_type', 'squared'))
        }
      }
    }
  }
)

export const feedSection = new schema.Entity('feedSections', { feeds: [feed], orphans: [feed] })

export const user = new schema.Entity(
  'users',
  { feeds: [feed] },
  {
    idAttribute: (value, parent, key) => value.user_id || value.id || value.user_info.user_id,
    processStrategy: u => {
      const user = { ...u, ...u.user_info, ...u.stats }
      return {
        ...omit(user, ['user_id', 'user_info', '', 'total_followers', 'total_followings', 'followByCurrentUser']),
        id: user.user_id || user.id,
        followersAmount: user.total_followers,
        followingsAmount: user.total_followings,
        followers: user.followers || [],
        followings: user.followings || [],
        followByCurrentUser: user.follow_by_current_user
      }
    }
  }
)

export const comment = new schema.Entity(
  'comments',
  { user },
  {
    processStrategy: c => {
      return {
        ...omit(c, ['user_id', 'user_bio', 'user_first_name', 'user_last_name', 'thumbnail_url', 'name']),
        user: {
          id: c.user_id,
          bio: c.user_bio,
          thumbnail_url: c.thumbnail_url,
          username: c.name,
          first_name: c.user_first_name,
          last_name: c.user_last_name,
          tagline: c.user_tagline
        }
      }
    }
  }
)

const cardParams = {
  key: 'cards',
  definition: {
    comments: [comment],
    first_comment: comment,
    first_owner_comment: comment,
    my_comment: comment,
    latest_comment: comment,
    referrer_comment: comment
  },
  options: {
    processStrategy: c => {
      return {
        ...omit(c, ['creator_bio', 'creator_full_name', 'creator_thumbnail', 'collection_thumbnails', 'media_tags']),
        thumbnails: {},
        referrerComments: {},
        userIds: uniq([c.latest_picker, ...(c.latest_who_liked || []), ...(c.pickers || [])]),
        creatorId: c.creator_id,
        cardInfo: c.card_info || {},
        collectionThumbnails: c.collection_thumbnails || [],
        pickers: c.pickers || [],
        view_settings: c.viewSettings || {}, // TO DO: check validity
        articleText: c.article_text,
        mediaTags: c.media_tags || []
      }
    }
  }
}

export const card = new schema.Entity(cardParams.key, cardParams.definition, cardParams.options)

export const collection = new schema.Entity(
  cardParams.key,
  { ...cardParams.definition, questions_collection: card, children_links: [card] },
  cardParams.options
)

comment.define({
  card
})

user.define({
  homeTabContent: [collection],
  picks: [collection],
  collections: { public: { arr: [collection] }, private: { arr: [collection] } },
  savedCards: [collection],
  pinnedFeeds: { arr: [collection] },
  pinnedPicks: { arr: [collection] },
  pinnedCollections: { arr: [collection] },
  allPinnedItems: [collection],
  followers: [user],
  followings: [user],
  feedSections: [feedSection]
})

export const tag = new schema.Entity(
  'tags',
  {},
  {
    idAttribute: value => value.id
  }
)

export const mediaTag = new schema.Entity(
  'mediaTags',
  {},
  {
    idAttribute: value => value.id
  }
)

export const activity = new schema.Entity(
  'activities',
  { user },
  {
    idAttribute: value => value.id,
    processStrategy: c => {
      return {
        ...omit(c, ['extra_user']),
        user: {
          id: c.extra_user.id,
          first_name: c.extra_user.first_name,
          last_name: c.extra_user.last_name,
          username: c.extra_user.username,
          thumbnail_url: c.extra_user.thumbnail_url
        }
      }
    }
  }
)
